import Clarity from '@microsoft/clarity';
import { hasTrackingConsent } from './cookieConsent';

const META_REQUIRES_COOKIE_CONSENT = process.env.NEXT_PUBLIC_META_REQUIRES_COOKIE_CONSENT === 'true';

const canUseBrowser = () => typeof window !== 'undefined';

const canTrackMeta = () => !META_REQUIRES_COOKIE_CONSENT || hasTrackingConsent();

const getPendingTrackingQueue = () => {
  if (!canUseBrowser()) return [];
  if (!Array.isArray(window.__tqPendingTrackingQueue)) {
    window.__tqPendingTrackingQueue = [];
  }
  return window.__tqPendingTrackingQueue;
};

const queueTrackingEvent = (provider, eventName, payload = {}) => {
  const queue = getPendingTrackingQueue();
  queue.push({ provider, eventName, payload });
  return true;
};

const sendQueuedEvent = ({ provider, eventName, payload }) => {
  if (provider === 'google') {
    if (typeof window.gtag !== 'function') return false;
    window.gtag('event', eventName, payload);
    return true;
  }

  if (provider === 'tiktok-page') {
    if (typeof window.ttq?.page !== 'function') return false;
    window.ttq.page();
    return true;
  }

  if (provider === 'meta') {
    if (typeof window.fbq !== 'function') return false;
    window.fbq('track', eventName, payload);
    return true;
  }

  if (provider === 'tiktok') {
    if (typeof window.ttq?.track !== 'function') return false;
    window.ttq.track(eventName, payload);
    return true;
  }

  if (provider === 'clarity-event') {
    if (typeof window.clarity !== 'function') return false;
    Clarity.event(eventName);
    return true;
  }

  if (provider === 'clarity-upgrade') {
    if (typeof window.clarity !== 'function') return false;
    Clarity.upgrade(eventName);
    return true;
  }

  return false;
};

const canSendQueuedProvider = (provider) => {
  if (provider === 'meta') return canTrackMeta();
  return hasTrackingConsent();
};

const sanitizeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getGooglePageViewPayload = () => ({
  page_title: document.title,
  page_location: window.location.href,
  page_path: `${window.location.pathname}${window.location.search}`,
});

export const buildContentItem = (item = {}) => ({
  content_id: item.id || '',
  content_name: item.name || '',
  content_category: item.category || 'Collection',
  quantity: sanitizeNumber(item.quantity || 1),
  price: sanitizeNumber(item.price),
});

export const buildContentItems = (items = []) => items.map(buildContentItem).filter((item) => item.content_id);

export const getCartValue = (items = []) =>
  items.reduce((total, item) => total + sanitizeNumber(item.price) * sanitizeNumber(item.quantity || 1), 0);

export const trackMetaEvent = (eventName, payload = {}) => {
  if (!canUseBrowser() || !canTrackMeta()) return false;
  if (typeof window.fbq !== 'function') return queueTrackingEvent('meta', eventName, payload);
  window.fbq('track', eventName, payload);
  return true;
};

export const trackGoogleEvent = (eventName, payload = {}) => {
  if (!canUseBrowser() || !hasTrackingConsent()) return false;
  if (typeof window.gtag !== 'function') return queueTrackingEvent('google', eventName, payload);
  window.gtag('event', eventName, payload);
  return true;
};

export const trackTikTokEvent = (eventName, payload = {}) => {
  if (!canUseBrowser() || !hasTrackingConsent()) return false;
  if (typeof window.ttq?.track !== 'function') return queueTrackingEvent('tiktok', eventName, payload);
  window.ttq.track(eventName, payload);
  return true;
};

export const trackTikTokPageView = () => {
  if (!canUseBrowser() || !hasTrackingConsent()) return false;
  if (typeof window.ttq?.page !== 'function') return queueTrackingEvent('tiktok-page', 'page');
  window.ttq.page();
  return true;
};

export const trackClarityEvent = (eventName) => {
  if (!eventName || !canUseBrowser() || !hasTrackingConsent()) return false;
  if (typeof window.clarity !== 'function') return queueTrackingEvent('clarity-event', eventName);
  Clarity.event(eventName);
  return true;
};

export const upgradeClaritySession = (reason) => {
  if (!reason || !canUseBrowser() || !hasTrackingConsent()) return false;
  if (typeof window.clarity !== 'function') return queueTrackingEvent('clarity-upgrade', reason);
  Clarity.upgrade(reason);
  return true;
};

export const flushTrackingQueue = () => {
  if (!canUseBrowser()) return false;

  const queue = getPendingTrackingQueue();
  if (!queue.length) return true;

  const remainingEvents = queue.filter((event) => !canSendQueuedProvider(event.provider) || !sendQueuedEvent(event));
  window.__tqPendingTrackingQueue = remainingEvents;

  return remainingEvents.length === 0;
};

export const trackPageView = () => {
  const googleTracked = trackGoogleEvent('page_view', getGooglePageViewPayload());
  const metaTracked = trackMetaEvent('PageView');
  const tiktokTracked = trackTikTokPageView();
  return googleTracked || metaTracked || tiktokTracked;
};

export const trackViewContent = (product) => {
  const value = sanitizeNumber(product?.price);
  const content = buildContentItem(product);
  const payload = {
    contents: [content],
    content_ids: [content.content_id],
    content_name: content.content_name,
    content_type: 'product',
    value,
    currency: 'KES',
  };

  const metaTracked = trackMetaEvent('ViewContent', payload);
  const tiktokTracked = trackTikTokEvent('ViewContent', payload);
  const clarityTracked = trackClarityEvent('product_viewed');
  return metaTracked || tiktokTracked || clarityTracked;
};

export const trackAddToCart = (product, quantity = 1) => {
  const content = buildContentItem({ ...product, quantity });
  const value = sanitizeNumber(product?.price) * sanitizeNumber(quantity);
  const payload = {
    contents: [content],
    content_ids: [content.content_id],
    content_name: content.content_name,
    content_type: 'product',
    value,
    currency: 'KES',
  };

  const metaTracked = trackMetaEvent('AddToCart', payload);
  const tiktokTracked = trackTikTokEvent('AddToCart', payload);
  const clarityTracked = trackClarityEvent('add_to_cart');
  const clarityUpgraded = upgradeClaritySession('cart_interaction');
  return metaTracked || tiktokTracked || clarityTracked || clarityUpgraded;
};

export const trackInitiateCheckout = (items = []) => {
  const contents = buildContentItems(items);
  const payload = {
    contents,
    content_ids: contents.map((item) => item.content_id),
    content_type: 'product',
    num_items: contents.reduce((total, item) => total + item.quantity, 0),
    value: getCartValue(items),
    currency: 'KES',
  };

  const metaTracked = trackMetaEvent('InitiateCheckout', payload);
  const tiktokTracked = trackTikTokEvent('InitiateCheckout', payload);
  const clarityTracked = trackClarityEvent('checkout_started');
  const clarityUpgraded = upgradeClaritySession('checkout_started');
  return metaTracked || tiktokTracked || clarityTracked || clarityUpgraded;
};

export const trackPurchase = (order) => {
  const items = order?.items || [];
  const contents = buildContentItems(items);
  const payload = {
    contents,
    content_ids: contents.map((item) => item.content_id),
    content_type: 'product',
    num_items: contents.reduce((total, item) => total + item.quantity, 0),
    value: sanitizeNumber(order?.total || getCartValue(items)),
    currency: 'KES',
    order_id: order?.id || '',
  };

  const metaTracked = trackMetaEvent('Purchase', payload);
  const tiktokTracked = trackTikTokEvent('CompletePayment', payload);
  const clarityTracked = trackClarityEvent('purchase_completed');
  const clarityUpgraded = upgradeClaritySession('purchase_completed');
  return metaTracked || tiktokTracked || clarityTracked || clarityUpgraded;
};
